import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut, RefreshCw, Check, CheckCheck, Calendar as CalendarIcon, Phone, Car,
  Loader2, Trash2, KeyRound, List as ListIcon, ChevronLeft, ChevronRight, MapPin, Ban,
} from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, parseISO,
} from "date-fns";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full rounded-none border-white/15 bg-zinc-900 text-white placeholder:text-zinc-600 focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary h-12";

const STATUS_STYLES = {
  pending: "border-amber-400/40 text-amber-300 bg-amber-400/10",
  confirmed: "border-teal-400/40 text-teal-300 bg-teal-400/10",
  completed: "border-purple-400/40 text-purple-300 bg-purple-400/10",
  cancelled: "border-zinc-500/40 text-zinc-400 bg-zinc-500/10 line-through",
};
const DOT = { pending: "bg-amber-400", confirmed: "bg-teal-400", completed: "bg-purple-400", cancelled: "bg-zinc-500" };
const SIZE_LABELS = { sedan: "Car / Sedan", van: "Van", suv_truck: "SUV / Truck" };
const ADDON_LABELS = { pet_hair: "Pet Hair", odor: "Odor Removal", excessive_dirt: "Excessive Dirt" };

function errText(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function CalendarView({ bookings }) {
  const [month, setMonth] = useState(new Date());
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month)),
    end: endOfWeek(endOfMonth(month)),
  });
  const forDay = (d) => bookings.filter((b) => {
    try { return isSameDay(parseISO(b.date), d); } catch { return false; }
  });

  return (
    <div data-testid="calendar-view" className="border border-white/10 bg-card p-4 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tight text-white">{format(month, "MMMM yyyy")}</h2>
        <div className="flex gap-2">
          <button data-testid="cal-prev" onClick={() => setMonth(subMonths(month, 1))} className="border border-white/15 p-2 text-white transition-colors hover:border-primary hover:text-primary"><ChevronLeft size={16} /></button>
          <button data-testid="cal-today" onClick={() => setMonth(new Date())} className="border border-white/15 px-3 py-2 font-mono-x text-[11px] uppercase tracking-widest text-white transition-colors hover:border-primary hover:text-primary">Today</button>
          <button data-testid="cal-next" onClick={() => setMonth(addMonths(month, 1))} className="border border-white/15 p-2 text-white transition-colors hover:border-primary hover:text-primary"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px border border-white/10 bg-white/10">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="bg-background py-2 text-center font-mono-x text-[10px] uppercase tracking-widest text-zinc-500">{d}</div>
        ))}
        {days.map((d) => {
          const items = forDay(d);
          const today = isSameDay(d, new Date());
          return (
            <div key={d.toISOString()} className={`min-h-[90px] bg-background p-2 ${isSameMonth(d, month) ? "" : "opacity-30"}`}>
              <div className={`mb-1 flex h-6 w-6 items-center justify-center font-mono-x text-xs ${today ? "gradient-fill text-black" : "text-zinc-400"}`}>{format(d, "d")}</div>
              <div className="space-y-1">
                {items.slice(0, 3).map((b) => (
                  <div key={b.id} data-testid={`cal-item-${b.id}`} className="flex items-center gap-1 truncate text-[10px] text-zinc-300">
                    <span className={`h-1.5 w-1.5 shrink-0 ${DOT[b.status]}`} />
                    <span className="truncate">{b.time.replace(/:00/, "")} {b.name.split(" ")[0]}</span>
                  </div>
                ))}
                {items.length > 3 ? <div className="text-[10px] text-primary">+{items.length - 3} more</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [view, setView] = useState("list");
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("vd_token") : null;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem("vd_token");
    nav("/login");
  }, [nav]);

  const load = useCallback(async () => {
    if (!token) { nav("/login"); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, authHeader);
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please log in."); logout(); }
      else toast.error("Could not load bookings.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setUpdating(id + status);
    try {
      const { data } = await axios.patch(`${API}/bookings/${id}`, { status }, authHeader);
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast.success(`Marked ${status}.`);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Update failed.");
    } finally { setUpdating(null); }
  };

  const removeBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, authHeader);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Booking deleted.");
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Delete failed.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, pw, authHeader);
      toast.success("Password updated.");
      setPwOpen(false);
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally { setPwLoading(false); }
  };

  const counts = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8 md:px-10 md:py-12">
      <div className="mx-auto max-w-[1400px]">
        {/* Header */}
        <div className="flex flex-col justify-between gap-6 border-b border-white/10 pb-8 md:flex-row md:items-end">
          <div>
            <div className="mb-3 flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rotate-45 gradient-fill" />
              <span className="font-cabinet text-lg font-black uppercase tracking-tight text-white">Velocity<span className="gradient-text">.</span></span>
            </div>
            <h1 className="font-cabinet text-4xl font-black uppercase leading-none tracking-tighter md:text-5xl">
              <span className="gradient-text">Bookings</span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex border border-white/15">
              <button data-testid="view-list" onClick={() => setView("list")} className={`flex items-center gap-2 px-4 py-3 font-mono-x text-xs uppercase tracking-widest transition-colors ${view === "list" ? "gradient-fill text-black" : "text-white hover:text-primary"}`}><ListIcon size={15} /> List</button>
              <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`flex items-center gap-2 px-4 py-3 font-mono-x text-xs uppercase tracking-widest transition-colors ${view === "calendar" ? "gradient-fill text-black" : "text-white hover:text-primary"}`}><CalendarIcon size={15} /> Calendar</button>
            </div>
            <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} className="flex items-center gap-2 border border-white/15 px-5 py-3 font-mono-x text-xs uppercase tracking-widest text-white transition-colors hover:border-primary hover:text-primary"><KeyRound size={15} /> Password</button>
            <button data-testid="refresh-btn" onClick={load} className="flex items-center gap-2 border border-white/15 px-5 py-3 font-mono-x text-xs uppercase tracking-widest text-white transition-colors hover:border-primary hover:text-primary"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</button>
            <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 border border-white/15 px-5 py-3 font-mono-x text-xs uppercase tracking-widest text-white transition-colors hover:border-red-500 hover:text-red-400"><LogOut size={15} /> Logout</button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 py-8 md:grid-cols-4">
          {[
            { k: "total", label: "Total" },
            { k: "pending", label: "Pending" },
            { k: "confirmed", label: "Confirmed" },
            { k: "completed", label: "Completed" },
          ].map((s) => (
            <div key={s.k} data-testid={`stat-${s.k}`} className="border border-white/10 bg-card p-5">
              <p className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-500">{s.label}</p>
              <p className="mt-2 font-cabinet text-4xl font-black gradient-text">{counts[s.k]}</p>
            </div>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 className="animate-spin" /></div>
        ) : view === "calendar" ? (
          <CalendarView bookings={bookings} />
        ) : bookings.length === 0 ? (
          <div data-testid="empty-state" className="border border-white/10 bg-card py-24 text-center">
            <p className="font-cabinet text-2xl font-bold uppercase text-white">No bookings yet</p>
            <p className="mt-2 text-zinc-500">New requests will appear here automatically.</p>
          </div>
        ) : (
          <div data-testid="bookings-list" className="space-y-4">
            {bookings.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                data-testid={`booking-row-${b.id}`}
                className="grid grid-cols-1 gap-4 border border-white/10 bg-card p-5 md:grid-cols-12 md:items-center md:p-6"
              >
                <div className="md:col-span-3">
                  <p className="font-cabinet text-xl font-bold uppercase tracking-tight text-white">{b.name}</p>
                  <p className="mt-1 flex items-center gap-2 text-sm text-zinc-400"><Phone size={13} className="text-primary" /> {b.phone}</p>
                  <p className="text-xs text-zinc-600">{b.email}</p>
                </div>
                <div className="md:col-span-3">
                  <p className="flex items-center gap-2 text-sm text-white"><Car size={14} className="text-primary" /> {b.vehicle}</p>
                  <p className="mt-1 font-mono-x text-xs uppercase tracking-widest text-zinc-400">{b.service} · ${b.price} · {SIZE_LABELS[b.vehicle_size] || b.vehicle_size}</p>
                  {b.addons && b.addons.length ? <p className="mt-1 text-xs text-primary">+ {b.addons.map((a) => ADDON_LABELS[a] || a).join(", ")}</p> : null}
                  {b.address ? <p className="mt-1 flex items-start gap-2 text-xs text-zinc-500"><MapPin size={12} className="mt-0.5 shrink-0 text-primary" /> {b.address}</p> : null}
                  {b.access_notes ? <p className="mt-1 text-xs italic text-zinc-600">Access: {b.access_notes}</p> : null}
                </div>
                <div className="md:col-span-2">
                  <p className="flex items-center gap-2 text-sm text-white"><CalendarIcon size={14} className="text-primary" /> {b.date}</p>
                  <p className="mt-1 text-xs text-zinc-500">{b.time}</p>
                  {b.notes ? <p className="mt-1 text-xs italic text-zinc-600">"{b.notes}"</p> : null}
                </div>
                <div className="md:col-span-1">
                  <span data-testid={`status-${b.id}`} className={`inline-block border px-3 py-1 font-mono-x text-[10px] uppercase tracking-widest ${STATUS_STYLES[b.status]}`}>{b.status}</span>
                </div>
                <div className="flex flex-wrap gap-2 md:col-span-3 md:justify-end">
                  <button data-testid={`confirm-${b.id}`} disabled={b.status === "confirmed" || updating === b.id + "confirmed"} onClick={() => setStatus(b.id, "confirmed")} className="flex items-center gap-2 border border-teal-400/40 px-4 py-2.5 font-mono-x text-[11px] uppercase tracking-widest text-teal-300 transition-colors hover:bg-teal-400/10 disabled:cursor-not-allowed disabled:opacity-30"><Check size={14} /> Confirm</button>
                  <button data-testid={`complete-${b.id}`} disabled={b.status === "completed" || updating === b.id + "completed"} onClick={() => setStatus(b.id, "completed")} className="flex items-center gap-2 border border-purple-400/40 px-4 py-2.5 font-mono-x text-[11px] uppercase tracking-widest text-purple-300 transition-colors hover:bg-purple-400/10 disabled:cursor-not-allowed disabled:opacity-30"><CheckCheck size={14} /> Complete</button>
                  <button data-testid={`cancel-${b.id}`} disabled={b.status === "cancelled" || updating === b.id + "cancelled"} onClick={() => setStatus(b.id, "cancelled")} className="flex items-center gap-2 border border-zinc-500/40 px-4 py-2.5 font-mono-x text-[11px] uppercase tracking-widest text-zinc-300 transition-colors hover:bg-zinc-500/10 disabled:cursor-not-allowed disabled:opacity-30"><Ban size={14} /> Cancel</button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-${b.id}`} className="flex items-center gap-2 border border-red-500/40 px-3 py-2.5 font-mono-x text-[11px] uppercase tracking-widest text-red-300 transition-colors hover:bg-red-500/10"><Trash2 size={14} /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-none border-white/15 bg-card text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-cabinet uppercase">Delete this booking?</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                          This permanently removes {b.name}'s {b.service} on {b.date}. This can't be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-delete-${b.id}`} onClick={() => removeBooking(b.id)} className="rounded-none bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Change password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="rounded-none border-white/15 bg-card text-white">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-2xl uppercase tracking-tight">Change Password</DialogTitle>
            <DialogDescription className="text-zinc-400">Update the password you use to sign in to this dashboard.</DialogDescription>
          </DialogHeader>
          <form onSubmit={changePassword} data-testid="change-password-form" className="space-y-5 pt-2">
            <div className="space-y-2">
              <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Current Password</Label>
              <Input data-testid="current-password" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={field} />
            </div>
            <div className="space-y-2">
              <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">New Password</Label>
              <Input data-testid="new-password" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="min 6 characters" className={field} />
            </div>
            <DialogFooter>
              <button type="submit" data-testid="save-password" disabled={pwLoading} className="flex w-full items-center justify-center gap-3 gradient-fill px-8 py-4 font-mono-x text-xs uppercase tracking-widest text-black transition-opacity duration-300 hover:opacity-90 disabled:opacity-60">
                {pwLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                {pwLoading ? "Saving..." : "Update Password"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
